// View-state for the Preview/Edit toggle in DetailPane, extracted as a pure
// reducer so the (previously bug-prone) transitions are testable.
//
// The bug this fixes: `editingPath` was only set when it was null, and was
// never cleared when the active article changed. So after editing article A,
// switching to article B and clicking "Edit MDX" kept showing A's MDX.
//
// Rules:
// - `open-edit` always targets the page you clicked from (no stale path).
// - `page-changed` (different article tab or different PR) exits the editor, so
//   the next "Edit MDX" opens the page you're actually looking at.
// - `show-preview` only hides the editor; it keeps `editingPath` so toggling
//   Preview↔Edit on the SAME article keeps the editor mounted and preserves
//   in-progress edits and pasted-image blobs.

export interface EditorViewState {
  editingPath: string | null;
  inEditView: boolean;
}

export type EditorViewAction =
  | { type: 'open-edit'; pagePath: string }
  | { type: 'show-preview' }
  | { type: 'page-changed' };

export const INITIAL_EDITOR_VIEW: EditorViewState = {
  editingPath: null,
  inEditView: false,
};

export function editorViewReducer(
  state: EditorViewState,
  action: EditorViewAction,
): EditorViewState {
  switch (action.type) {
    case 'open-edit':
      return { editingPath: action.pagePath, inEditView: true };
    case 'show-preview':
      return state.editingPath ? { ...state, inEditView: false } : state;
    case 'page-changed':
      // Return the shared constant so useReducer can bail out of a re-render
      // when we're already on the initial (preview) view.
      return state.editingPath === null && !state.inEditView
        ? state
        : INITIAL_EDITOR_VIEW;
  }
}
